import React from "react";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import { OrderClient } from "@types";
import LoadingButton from "@mui/lab/LoadingButton";
import { CircularProgress } from "@mui/material";
import UpdateClientOrderClientMutation from "./UpdateClientOrderClientMutation.gql";
import ClientPortalConfigQuery from "pages/Delivery/ClientPortalConfigQuery.gql";
import {
  createValidationSchema,
  getClientInformationValidationValues,
  hasExistingGpsCoordinates,
  isClientInformationComplete,
} from "./validationSchema";
import PostalCodeAndCityAutoComplete from "./PostalCodeAndCityAutoComplete";
import PostalCodesAndCitiesQuery from "./PostalCodeAndCityAutoComplete/PostalCodesAndCitiesQuery.gql";
import FrenchAddressAutocomplete, {
  AddressResult,
} from "./FrenchAddressAutocomplete";
import AlertDialog from "components/AlertDialog";

export interface ClientInformationFormState {
  firstname: string;
  lastname: string;
  address: string;
  address_addition: string;
  postal_code_city: string;
  floor: number | string;
  email: string;
  phone: string;
}

interface ClientInformationFormProps {
  client: OrderClient;
  setIsCoordonneesComplete: (value: boolean) => void;
}

const parseFloorValue = (value: string) => (value === "" ? "" : Number(value));

const ClientInformationForm = ({
  client,
  setIsCoordonneesComplete,
}: ClientInformationFormProps) => {
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const selectedAddressGpsRef = React.useRef<{
    lat: number;
    lng: number;
  } | null>(null);
  const { loading: configLoading, data: clientPortalConfigData } = useQuery(
    ClientPortalConfigQuery,
  );

  // Check if the client already has GPS coordinates in the database (backwards compatibility)
  const hasExistingGps = React.useMemo(() => {
    return hasExistingGpsCoordinates(client);
  }, [client]);

  // If client already has GPS coordinates, initialize the ref with them
  React.useEffect(() => {
    if (hasExistingGps && client.lat != null && client.lng != null) {
      selectedAddressGpsRef.current = { lat: client.lat, lng: client.lng };
    }
  }, [hasExistingGps, client.lat, client.lng]);

  // Create validation schema based on whether client has existing GPS coords
  const validationSchema = React.useMemo(
    () => createValidationSchema(hasExistingGps),
    [hasExistingGps],
  );

  const formLocked = React.useMemo(() => {
    return !clientPortalConfigData?.clientPortalConfig?.contractorConfig
      ?.is_client_info_modifiable_in_client_portal;
  }, [
    clientPortalConfigData?.clientPortalConfig?.contractorConfig
      ?.is_client_info_modifiable_in_client_portal,
  ]);

  const initialValues: ClientInformationFormState = React.useMemo(
    () => getClientInformationValidationValues(client),
    [client],
  );

  const [updateClientOrderClient, { loading }] = useMutation(
    UpdateClientOrderClientMutation,
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: (formValues) => {
      const { postal_code_city, floor, ...clientOrderClient } = formValues;
      const [postal_code, city] = postal_code_city.split(" - ");
      const normalizedFloor = typeof floor === "number" ? floor : Number(floor);

      // If the address was selected from autocomplete, send GPS coords to backend
      const clientGps = selectedAddressGpsRef.current;
      const clientOrderClientWithGps = clientGps
        ? {
            ...clientOrderClient,
            floor: normalizedFloor,
            lat: clientGps.lat,
            lng: clientGps.lng,
          }
        : { ...clientOrderClient, floor: normalizedFloor };

      updateClientOrderClient({
        variables: {
          clientOrderClient: {
            ...clientOrderClientWithGps,
            postal_code,
            city,
          },
        },
        onCompleted: (data) => {
          const updatedClient =
            data?.updateClientOrderClient?.clientOrder?.client;

          if (updatedClient) {
            formik.resetForm({
              values: getClientInformationValidationValues(updatedClient),
            });
            setIsCoordonneesComplete(
              isClientInformationComplete(updatedClient),
            );
          }

          toast.success("Vos informations ont bien été enregistrées");
        },
        onError: () =>
          toast.error(
            "Une erreur est survenue, vos informations n'ont pas été mises à jours",
          ),
      });
    },
  });

  // Query to search for matching postal code in our database
  const [searchPostalCode] = useLazyQuery(PostalCodesAndCitiesQuery);

  // Handle address selection from the autocomplete
  const handleAddressSelect = React.useCallback(
    async (address: AddressResult | null) => {
      if (!address) {
        formik.setFieldValue("address", "");
        selectedAddressGpsRef.current = null;
        return;
      }

      // Preserve house number: take everything before the first comma
      // Example: "48 Chemin de la Butte, 75000 Paris" -> "48 Chemin de la Butte"
      let streetAddress = (address.fulltext || "").trim();
      if (streetAddress.includes(",")) {
        streetAddress = streetAddress.split(",")[0].trim();
      }
      if (!streetAddress && address.street) {
        streetAddress = address.street;
      }

      formik.setFieldValue("address", streetAddress);

      // Save GPS coords if we have them (backend expects lat/lng)
      const lat = address.coordinates?.lat;
      const lng = address.coordinates?.lon;
      if (typeof lat === "number" && typeof lng === "number") {
        selectedAddressGpsRef.current = { lat, lng };
      } else if (address.geometry?.coordinates) {
        // GeoJSON format: [lon, lat]
        selectedAddressGpsRef.current = {
          lat: address.geometry.coordinates[1],
          lng: address.geometry.coordinates[0],
        };
      } else if (
        typeof address.y === "number" &&
        typeof address.x === "number"
      ) {
        // GeoPF completion sometimes uses x/y where y=lat, x=lon
        selectedAddressGpsRef.current = { lat: address.y, lng: address.x };
      } else {
        selectedAddressGpsRef.current = null;
      }

      // Try to match the postal code from the API with our database
      if (address.zipcode) {
        try {
          const { data } = await searchPostalCode({
            variables: { query: address.zipcode },
          });

          if (data?.clientPostalCodesAndCities?.length > 0) {
            const postalCodes = data.clientPostalCodesAndCities;

            // Try to find an exact match on city name
            let matchedPostalCode = postalCodes.find(
              (pc: { postal_code: string; city: string }) =>
                pc.postal_code === address.zipcode &&
                pc.city.toLowerCase() === address.city?.toLowerCase(),
            );

            // If no exact match, try case-insensitive partial match
            if (!matchedPostalCode && address.city) {
              matchedPostalCode = postalCodes.find(
                (pc: { postal_code: string; city: string }) =>
                  pc.postal_code === address.zipcode &&
                  (pc.city
                    .toLowerCase()
                    .includes(address.city!.toLowerCase()) ||
                    address
                      .city!.toLowerCase()
                      .includes(pc.city.toLowerCase())),
              );
            }

            // If still no match, just take the first one with matching postal code
            if (!matchedPostalCode) {
              matchedPostalCode = postalCodes.find(
                (pc: { postal_code: string; city: string }) =>
                  pc.postal_code === address.zipcode,
              );
            }

            if (matchedPostalCode) {
              formik.setFieldValue(
                "postal_code_city",
                `${matchedPostalCode.postal_code} - ${matchedPostalCode.city}`,
              );
            }
          }
        } catch (error) {
          console.error("Error searching for postal code:", error);
        }
      }
    },
    [formik, searchPostalCode],
  );

  React.useEffect(() => {
    setIsCoordonneesComplete(formik.isValid);
  }, [formik.isValid, setIsCoordonneesComplete]);

  const handleSubmitForm = (event: any) => {
    event.preventDefault();

    if (formik.values.postal_code_city !== initialValues.postal_code_city) {
      setOpenDialog(true);
    } else {
      formik.handleSubmit();
    }
  };

  const isFormDataChanged = React.useMemo<boolean>(() => {
    return formik.dirty;
  }, [formik.dirty]);

  const isFloorMissing = formik.values.floor === "";
  const isFloorInvalid =
    typeof formik.values.floor === "number" &&
    Number.isNaN(formik.values.floor);
  const showFloorError =
    isFloorMissing ||
    ((Boolean(formik.touched.floor) || formik.submitCount > 0) &&
      isFloorInvalid);
  const floorHelperText = isFloorMissing
    ? "L'étage est requis."
    : showFloorError
    ? formik.errors.floor
    : undefined;

  if (configLoading) {
    return (
      <div className="client-information-form__loading">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <form className="client-information-form" onSubmit={handleSubmitForm}>
        <div className="client-information-form__card">
          <div className="client-information-form__card-header">
            <svg
              className="client-information-form__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            <span>Informations de livraison</span>
          </div>
          <div className="client-information-form__card-body">
            <div className="client-information-form__fields">
              <TextField
                required
                fullWidth
                label="Nom"
                variant="outlined"
                name="lastname"
                value={formik.values.lastname}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastname && Boolean(formik.errors.lastname)
                }
                helperText={formik.touched.lastname && formik.errors.lastname}
                disabled={true}
              />
              <TextField
                required
                fullWidth
                label="Prénom"
                variant="outlined"
                name="firstname"
                value={formik.values.firstname}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstname && Boolean(formik.errors.firstname)
                }
                helperText={formik.touched.firstname && formik.errors.firstname}
                disabled={true}
              />
              <FrenchAddressAutocomplete
                value={formik.values.address}
                onChange={handleAddressSelect}
                onTextChange={(val: string) => {
                  // Only clear GPS if we didn't have existing GPS coordinates
                  // This allows users with existing GPS to modify the address text without losing coords
                  if (!hasExistingGps) {
                    selectedAddressGpsRef.current = null;
                  }
                  formik.setFieldValue("address", val);
                }}
                label="Adresse de livraison"
                placeholder={
                  hasExistingGps
                    ? "Adresse actuelle (modifiable)"
                    : "Commencez à taper et sélectionnez une adresse"
                }
                required={!hasExistingGps}
                fullWidth
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                disabled={formLocked}
              />
              <TextField
                fullWidth
                label="Complément d'adresse"
                variant="outlined"
                name="address_addition"
                value={formik.values.address_addition}
                onChange={formik.handleChange}
                disabled={formLocked}
              />
              <PostalCodeAndCityAutoComplete
                value={formik.values.postal_code_city}
                onChange={async (_, value: string | null) => {
                  selectedAddressGpsRef.current = null;
                  await formik.setFieldValue("postal_code_city", value);
                }}
                error={
                  formik.touched.postal_code_city &&
                  Boolean(formik.errors.postal_code_city)
                }
                helperText={
                  formik.touched.postal_code_city &&
                  formik.errors.postal_code_city
                }
                disabled={formLocked}
              />
              <TextField
                required
                fullWidth
                label="Étage"
                variant="outlined"
                type="number"
                name="floor"
                value={formik.values.floor}
                onChange={(event) => {
                  formik.setFieldValue(
                    "floor",
                    parseFloorValue(event.target.value),
                  );
                }}
                onBlur={formik.handleBlur}
                error={showFloorError}
                helperText={floorHelperText}
                disabled={formLocked}
              />
              <TextField
                required
                fullWidth
                label="N° de tél"
                variant="outlined"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                disabled={formLocked}
              />
              <TextField
                required
                fullWidth
                label="Email"
                variant="outlined"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={formLocked}
              />
            </div>
            {!formLocked && (
              <div className="client-information-form__action">
                <LoadingButton
                  type="submit"
                  loading={loading}
                  variant="contained"
                  size="large"
                  disabled={!isFormDataChanged}
                >
                  Enregistrer les modifications
                </LoadingButton>
              </div>
            )}
          </div>
        </div>
      </form>
      <AlertDialog
        handleClose={() => setOpenDialog(false)}
        open={openDialog}
        callback={formik.handleSubmit}
        title="Attention !"
        message="Si vous modifiez le code postal le créneau choisi sera réinitialisé et devra être sélectionné de nouveau. Ignorez cette alerte si vous n'avez pas encore sélectionné de créneau."
        appearence="alert"
      />
    </>
  );
};

export default ClientInformationForm;
